import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Stack,
  Title,
  Paper,
  Button,
  Group,
  Alert,
  Text,
  Card,
  Badge,
  Table,
  ActionIcon,
  Tooltip,
  TextInput,
  Skeleton,
} from '@mantine/core';
import {
  IconRefresh,
  IconDatabase,
  IconClock,
  IconTicket,
  IconCopy,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAppUser } from '../../hooks/useAppUser';
import { usePageTitle } from '../../hooks/usePageTitle';

type Voucher = {
  id: string;
  isUsed: boolean;
  usedBy?: string;
  usedAt?: string;
  createdAt: string;
  createdBy?: string;
  description?: string;
};

export const AdminVouchersPage = () => {
  usePageTitle('Admin Vouchers | Meals You Love');
  const { user } = useAppUser();
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);
  const [creatingVoucher, setCreatingVoucher] = useState(false);
  const [voucherDescription, setVoucherDescription] = useState('');

  const loadVouchers = useCallback(async () => {
    if (!user) return;

    setVouchersLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/vouchers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setVouchers(data.vouchers);
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to load vouchers',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load vouchers',
        color: 'red',
      });
    } finally {
      setVouchersLoading(false);
    }
  }, [user]);

  const handleCreateVoucher = async () => {
    if (!user) return;

    setCreatingVoucher(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/vouchers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ description: voucherDescription || undefined }),
      });

      if (response.ok) {
        const data = await response.json();
        notifications.show({
          title: 'Voucher created',
          message: `Voucher code: ${data.voucher.id}`,
          color: 'green',
          autoClose: false,
        });
        setVoucherDescription('');
        loadVouchers();
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Error',
          message: error.error || 'Failed to create voucher',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to create voucher',
        color: 'red',
      });
    } finally {
      setCreatingVoucher(false);
    }
  };

  useEffect(() => {
    if (!user?.isAdmin) return;
    loadVouchers();
  }, [user, loadVouchers]);

  return (
    <Container size="xl" py="xl">
      <Paper withBorder p="md" radius="lg">
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <div>
              <Title order={3}>Voucher Codes</Title>
              <Text size="sm" c="dimmed">
                Create and manage promotional voucher codes
              </Text>
            </div>
            <Button
              leftSection={<IconRefresh size="1rem" />}
              variant="outline"
              onClick={loadVouchers}
              loading={vouchersLoading}
            >
              Refresh
            </Button>
          </Group>

          <Card withBorder radius="lg">
            <Stack gap="sm">
              <Text fw={500}>Create New Voucher</Text>
              {/* flex-basis so the button wraps below the input on phones. */}
              <Group align="flex-end">
                <TextInput
                  label="Description (optional)"
                  placeholder="e.g., Launch promo, Beta tester reward"
                  value={voucherDescription}
                  onChange={(e) => setVoucherDescription(e.currentTarget.value)}
                  style={{ flex: '1 1 220px' }}
                />
                <Button
                  leftSection={<IconTicket size="1rem" />}
                  onClick={handleCreateVoucher}
                  loading={creatingVoucher}
                >
                  Generate Voucher
                </Button>
              </Group>
            </Stack>
          </Card>

          {vouchersLoading && vouchers.length === 0 ? (
            <Stack gap="xs">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={40} radius="sm" />
              ))}
            </Stack>
          ) : vouchers.length === 0 ? (
            <Alert
              icon={<IconDatabase size="1rem" />}
              title="No Vouchers"
              color="blue"
              radius="lg"
            >
              No vouchers have been created yet.
            </Alert>
          ) : (
            <Table.ScrollContainer minWidth={760}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Voucher Code</Table.Th>
                    <Table.Th>Description</Table.Th>
                    {/* miw so the status badge can't be squeezed into "..." */}
                    <Table.Th miw={95}>Status</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th>Used By</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {vouchers.map((voucher) => (
                    <Table.Tr key={voucher.id}>
                      <Table.Td>
                        <Group gap="xs">
                          <Text fw={500} size="sm" style={{ fontFamily: 'monospace' }}>
                            {voucher.id}
                          </Text>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            onClick={() => {
                              navigator.clipboard.writeText(voucher.id);
                              notifications.show({
                                title: 'Copied',
                                message: 'Voucher code copied to clipboard',
                                color: 'green',
                              });
                            }}
                          >
                            <IconCopy size="1rem" />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{voucher.description || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={voucher.isUsed ? 'gray' : 'green'}
                          variant="light"
                        >
                          {voucher.isUsed ? 'Used' : 'Available'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {new Date(voucher.createdAt).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {voucher.usedBy || '-'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {voucher.isUsed && voucher.usedAt && (
                          <Tooltip label={`Used on ${new Date(voucher.usedAt).toLocaleString()}`}>
                            <ActionIcon size="sm" variant="subtle" color="gray">
                              <IconClock size="1rem" />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};
